import React from "react";
import {
  Modal,
  Box,
  Typography,
  Slide,
  Grid,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Divider,
  Button,
} from "@mui/material";
import {
  LocationOn,
  CalendarToday,
  Work,
  AttachMoney,
  Person,
  Business,
  Assessment,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";

export default function JobViewMoreModal({ open, handleClose, viewData }) {
  if (!viewData) return null;

  const formatDate = (dateString, fallback = "N/A") => {
    if (!dateString) return fallback;
    const d = new Date(dateString);
    if (isNaN(d)) return dateString;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}-${mm}-${d.getFullYear()}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const d = new Date(dateString);
    if (isNaN(d)) return dateString;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${d.getFullYear()} ${hh}:${min}`;
  };

  // Parse JSON strings
  const parseJsonArray = (jsonString) => {
    try {
      return JSON.parse(jsonString);
    } catch {
      return [];
    }
  };

  const skills = viewData.skill_names || [];
  const locations = parseJsonArray(viewData.location);
  const interviewRounds = parseJsonArray(viewData.interview_rounds);

  // Parse requirements string
  const parseRequirements = (reqString) => {
    if (!reqString || reqString.length < 6) return {};
    return {
      photo: reqString[0] === "1",
      resume: reqString[1] === "1",
      dateOfBirth: reqString[2] === "1",
      gender: reqString[3] === "1",
      currentCTC: reqString[4] === "1",
      expectedCTC: reqString[5] === "1",
    };
  };

  const requirements = parseRequirements(viewData.requirements);

  // Format currency
  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case 1:
        return "$";
      case 2:
        return "€";
      case 3:
        return "₹";
      default:
        return "$";
    }
  };

  // Format salary display
  const formatSalary = () => {
    const symbol = getCurrencySymbol(viewData.currency);
    if (viewData.pay_by === 1 && viewData.min_salary && viewData.max_salary) {
      return `${symbol}${viewData.min_salary?.toLocaleString()} - ${symbol}${viewData.max_salary?.toLocaleString()}`;
    } else if (viewData.rate) {
      return `${symbol}${viewData.rate?.toLocaleString()}`;
    }
    return "Not specified";
  };

  return (
    <Modal open={open} onClose={handleClose} closeAfterTransition>
      <Slide direction="left" in={open}>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "75%",
            height: "100%",
            bgcolor: "#f5f5f5",
            boxShadow: 24,
            p: 4,
            overflow: "auto",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h3" component="h2" sx={{ color: "#212121" }}>
              Job Details - {viewData.title}
            </Typography>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderColor: "#bdbdbd",
                color: "#424242",
                "&:hover": {
                  borderColor: "#9e9e9e",
                  bgcolor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Close
            </Button>
          </Box>

          <Grid container spacing={3}>
            {/* Basic Information Card */}
            <Grid item xs={12} md={8}>
              <Card
                sx={{
                  p: 2,
                  bgcolor: "#ffffff",
                  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
                  mb: 3,
                }}
              >
                <CardContent>
                  <Typography variant="h5" sx={{ color: "#212121", mb: 2 }}>
                    Basic Information
                  </Typography>
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ bgcolor: "transparent" }}
                  >
                    <Table sx={{ bgcolor: "transparent" }}>
                      <TableBody>
                        <TableRow sx={{ "&:last-child td": { border: 0 } }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#424242",
                              bgcolor: "transparent",
                              border: "none",
                              borderBottom: "1px solid #e0e0e0",
                              py: 1.5,
                            }}
                          >
                            Job Title
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "#212121",
                              bgcolor: "transparent",
                              border: "none",
                              borderBottom: "1px solid #e0e0e0",
                              py: 1.5,
                            }}
                          >
                            {viewData.title}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ "&:last-child td": { border: 0 } }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#424242",
                              bgcolor: "transparent",
                              border: "none",
                              borderBottom: "1px solid #e0e0e0",
                              py: 1.5,
                            }}
                          >
                            Department
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "#212121",
                              bgcolor: "transparent",
                              border: "none",
                              borderBottom: "1px solid #e0e0e0",
                              py: 1.5,
                            }}
                          >
                            <Box display="flex" alignItems="center">
                              <Business
                                fontSize="small"
                                sx={{ mr: 1, color: "#757575" }}
                              />
                              {viewData.department?.department || "N/A"}
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ "&:last-child td": { border: 0 } }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#424242",
                              bgcolor: "transparent",
                              border: "none",
                              borderBottom: "1px solid #e0e0e0",
                              py: 1.5,
                            }}
                          >
                            Status
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "#212121",
                              bgcolor: "transparent",
                              border: "none",
                              borderBottom: "1px solid #e0e0e0",
                              py: 1.5,
                            }}
                          >
                            <Box display="flex" alignItems="center">
                              {viewData.status === "Open" ? (
                                <CheckCircle
                                  sx={{ color: "#4caf50", mr: 1 }}
                                  fontSize="small"
                                />
                              ) : (
                                <Cancel
                                  sx={{ color: "#f44336", mr: 1 }}
                                  fontSize="small"
                                />
                              )}
                              {viewData.status}
                            </Box>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ "&:last-child td": { border: 0 } }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#424242",
                              bgcolor: "transparent",
                              border: "none",
                              borderBottom: "1px solid #e0e0e0",
                              py: 1.5,
                            }}
                          >
                            Total Openings
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "#212121",
                              bgcolor: "transparent",
                              border: "none",
                              borderBottom: "1px solid #e0e0e0",
                              py: 1.5,
                            }}
                          >
                            {viewData.vacancy}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ "&:last-child td": { border: 0 } }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#424242",
                              bgcolor: "transparent",
                              border: "none",
                              borderBottom: "1px solid #e0e0e0",
                              py: 1.5,
                            }}
                          >
                            Remote Job
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "#212121",
                              bgcolor: "transparent",
                              border: "none",
                              borderBottom: "1px solid #e0e0e0",
                              py: 1.5,
                            }}
                          >
                            {viewData.remote === 1 ? "Yes" : "No"}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ "&:last-child td": { border: 0 } }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "#424242",
                              bgcolor: "transparent",
                              border: "none",
                              py: 1.5,
                            }}
                          >
                            Salary Disclosed
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "#212121",
                              bgcolor: "transparent",
                              border: "none",
                              py: 1.5,
                            }}
                          >
                            {viewData.salary === 1 ? "Yes" : "No"}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Quick Stats Card */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  p: 2,
                  bgcolor: "#ffffff",
                  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
                  mb: 3,
                }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ color: "#212121", mb: 2 }}>
                    Quick Stats
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" alignItems="center">
                      <CalendarToday
                        fontSize="small"
                        sx={{ mr: 1, color: "#757575" }}
                      />
                      <Box>
                        <Typography variant="body2" sx={{ color: "#757575" }}>
                          Start Date
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#212121" }}>
                          {formatDate(viewData.start_date)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <CalendarToday
                        fontSize="small"
                        sx={{ mr: 1, color: "#757575" }}
                      />
                      <Box>
                        <Typography variant="body2" sx={{ color: "#757575" }}>
                          End Date
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#212121" }}>
                          {formatDate(viewData.end_date, "No end date")}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <AttachMoney
                        fontSize="small"
                        sx={{ mr: 1, color: "#757575" }}
                      />
                      <Box>
                        <Typography variant="body2" sx={{ color: "#757575" }}>
                          Salary
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#212121" }}>
                          {formatSalary()}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Recruiter Information */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  p: 2,
                  bgcolor: "#ffffff",
                  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
                  mb: 3,
                }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ color: "#212121", mb: 2 }}>
                    Recruiter Information
                  </Typography>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Person fontSize="small" sx={{ mr: 1, color: "#757575" }} />
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{ color: "#212121", fontWeight: 600 }}
                      >
                        {viewData.recruiter?.name || "N/A"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#757575" }}>
                        ID: {viewData.recruiter?.empid || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Skills */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  p: 2,
                  bgcolor: "#ffffff",
                  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
                  mb: 3,
                }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ color: "#212121", mb: 2 }}>
                    Required Skills
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {skills.length > 0 ? (
                      skills.map((skill, index) => (
                        <Chip
                          key={index}
                          label={skill}
                          variant="outlined"
                          size="small"
                          sx={{
                            bgcolor: "#e3f2fd",
                            borderColor: "#90caf9",
                            color: "#000000",
                            "& .MuiChip-label": {
                              color: "#000000",
                            },
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ color: "#757575" }}>
                        No skills specified
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Job Description */}
            <Grid item xs={12}>
              <Card
                sx={{
                  p: 2,
                  bgcolor: "#ffffff",
                  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
                  mb: 3,
                }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ color: "#212121", mb: 2 }}>
                    Job Description
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#424242",
                      lineHeight: 1.6,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {viewData.description || "No description provided"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Requirements */}
            <Grid item xs={12}>
              <Card
                sx={{
                  p: 2,
                  bgcolor: "#ffffff",
                  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
                  mb: 3,
                }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ color: "#212121", mb: 2 }}>
                    Application Requirements
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries({
                      Photo: requirements.photo,
                      Resume: requirements.resume,
                      "Date of Birth": requirements.dateOfBirth,
                      Gender: requirements.gender,
                      "Current CTC": requirements.currentCTC,
                      "Expected CTC": requirements.expectedCTC,
                    }).map(([requirement, isRequired]) => (
                      <Grid item xs={12} sm={6} md={4} key={requirement}>
                        <Box display="flex" alignItems="center">
                          {isRequired ? (
                            <CheckCircle
                              sx={{ color: "#4caf50", mr: 1 }}
                              fontSize="small"
                            />
                          ) : (
                            <Cancel
                              sx={{ color: "#f44336", mr: 1 }}
                              fontSize="small"
                            />
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              color: isRequired ? "#4caf50" : "#f44336",
                              fontWeight: isRequired ? 600 : 400,
                            }}
                          >
                            {requirement}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Additional Information */}
            <Grid item xs={12}>
              <Card
                sx={{
                  p: 2,
                  bgcolor: "#ffffff",
                  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
                }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ color: "#212121", mb: 2 }}>
                    Additional Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="body2"
                        sx={{ color: "#757575", mb: 1 }}
                      >
                        Created By
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#212121" }}>
                        {viewData.created_by}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="body2"
                        sx={{ color: "#757575", mb: 1 }}
                      >
                        Created At
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#212121" }}>
                        {formatDateTime(viewData.created_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="body2"
                        sx={{ color: "#757575", mb: 1 }}
                      >
                        Last Updated
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#212121" }}>
                        {formatDateTime(viewData.updated_at)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Slide>
    </Modal>
  );
}
